import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import JobCreateRichTextField from "./JobCreateRichTextField";
import { listHiringApplicantInvites, postHiringApplicantInvites } from "../apiClient";
import {
  APPLICANT_INVITE_LINK_INTRO,
  APPLICANT_INVITE_PLACEHOLDER_APPLICANT_NAME,
  APPLICANT_INVITE_PLACEHOLDER_APPLICATION_LINK,
  APPLICANT_INVITE_PLACEHOLDER_COMPANY_NAME,
  APPLICANT_INVITE_PLACEHOLDER_HIRING_MANAGER_NAME,
  APPLICANT_INVITE_CTA_LABEL,
  APPLICANT_INVITE_DISCLAIMER,
  buildApplicantInviteBodyHtml,
  buildApplicantInviteCtaButtonHtml,
  DEFAULT_APPLICANT_INVITE_FIXED_CLOSING_HTML,
  DEFAULT_APPLICANT_INVITE_SUBJECT,
  defaultInviteMessageHtmlForEditor,
  downloadApplicantInviteCsvTemplate,
  highlightInvitePlaceholderTokensInHtml,
  INVITE_EMAIL_TOKEN_CLASS,
  isValidInviteEmail,
  parseApplicantInviteCsv,
  stripInviteTokenHighlightSpans,
  validRecipientsFromCsvRows,
} from "../data/applicantInviteHelpers";
import { getEditorTextLength, normalizeRichTextStorage } from "../lib/richTextEditor";
import { useTawkToSuppressWhile } from "../context/TawkToContext";

const INVITE_MESSAGE_MAX_CHARS = 3500;
const INVITE_LARGE_BATCH_CONFIRM_THRESHOLD = 25;

const EMPTY_INVITE_LOG_SUMMARY = { total: 0, sent: 0, failed: 0, pending: 0 };

const EMPTY_MANUAL_ROW = { name: "", email: "" };

const INVITE_TABS = [
  { id: "new-applicants", label: "New invites" },
  { id: "invitation-email", label: "Invitation email" },
  { id: "invited-log", label: "Invited applicant log" },
];

function mergeRecipients(csvRows, manualRows) {
  const merged = [];
  const seen = new Set();

  [...csvRows, ...manualRows].forEach((row) => {
    const name = String(row?.name || "").trim();
    const email = String(row?.email || "").trim().toLowerCase();
    if (!name || !email || !isValidInviteEmail(email) || seen.has(email)) return;
    seen.add(email);
    merged.push({ name, email });
  });

  return merged;
}

function formatInviteDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** Pending/failed → queued at createdAt; sent → worker sets sentAt when email goes out. */
function inviteLastUpdatedAt(invite) {
  if (invite?.status === "sent" && invite.sentAt) return invite.sentAt;
  return invite?.createdAt || null;
}

function inviteStatusLabel(status) {
  if (status === "sent") return "Sent";
  if (status === "failed") return "Failed";
  if (status === "pending") return "Pending";
  return String(status || "—");
}

function normalizeInviteLogSummary(summary, invites) {
  if (
    summary &&
    typeof summary.total === "number" &&
    typeof summary.sent === "number" &&
    typeof summary.failed === "number" &&
    typeof summary.pending === "number"
  ) {
    return summary;
  }
  const list = Array.isArray(invites) ? invites : [];
  return {
    total: list.length,
    sent: list.filter((row) => row.status === "sent").length,
    failed: list.filter((row) => row.status === "failed").length,
    pending: list.filter((row) => row.status === "pending").length,
  };
}

function formatInviteLogSummary(summary) {
  const s = normalizeInviteLogSummary(summary);
  return `${s.total} invited · ${s.sent} sent · ${s.failed} failed · ${s.pending} pending`;
}

function InviteEmailToken({ children }) {
  return <span className={INVITE_EMAIL_TOKEN_CLASS}>{children}</span>;
}

function InviteEmailSignatureLine({ hiringManagerName, companyName }) {
  const manager = String(hiringManagerName || "").trim();
  const company = String(companyName || "").trim();

  if (manager && company) {
    return (
      <>
        {manager} & the team at {company}
      </>
    );
  }

  if (manager) {
    return (
      <>
        {manager} & the team at{" "}
        <InviteEmailToken>{APPLICANT_INVITE_PLACEHOLDER_COMPANY_NAME}</InviteEmailToken>
      </>
    );
  }

  if (company) {
    return (
      <>
        <InviteEmailToken>{APPLICANT_INVITE_PLACEHOLDER_HIRING_MANAGER_NAME}</InviteEmailToken> & the team at{" "}
        {company}
      </>
    );
  }

  return (
    <>
      <InviteEmailToken>{APPLICANT_INVITE_PLACEHOLDER_HIRING_MANAGER_NAME}</InviteEmailToken> & the team at{" "}
      <InviteEmailToken>{APPLICANT_INVITE_PLACEHOLDER_COMPANY_NAME}</InviteEmailToken>
    </>
  );
}

export default function InviteApplicantsModal({
  open,
  onRequestClose,
  jobActive = false,
  jobPostId = "",
  jobTitle = "",
  jobPublicSlug = "",
  authToken = "",
  hiringManagerName = "",
  companyName = "",
  initialTab = null,
}) {
  useTawkToSuppressWhile(open);

  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("new-applicants");
  const [manualRows, setManualRows] = useState([{ ...EMPTY_MANUAL_ROW }]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvParseErrors, setCsvParseErrors] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [subject, setSubject] = useState(DEFAULT_APPLICANT_INVITE_SUBJECT);
  const [messageHtml, setMessageHtml] = useState(defaultInviteMessageHtmlForEditor);
  const [sendError, setSendError] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [invitedLog, setInvitedLog] = useState([]);
  const [invitedLogSummary, setInvitedLogSummary] = useState(EMPTY_INVITE_LOG_SUMMARY);
  const [invitedLogLoading, setInvitedLogLoading] = useState(false);
  const [invitedLogError, setInvitedLogError] = useState("");

  const messageCharsRemaining = Math.max(0, INVITE_MESSAGE_MAX_CHARS - getEditorTextLength(messageHtml));

  const resetForm = useCallback(() => {
    setActiveTab("new-applicants");
    setManualRows([{ ...EMPTY_MANUAL_ROW }]);
    setCsvRows([]);
    setCsvParseErrors([]);
    setCsvFileName("");
    setSubject(DEFAULT_APPLICANT_INVITE_SUBJECT);
    setMessageHtml(defaultInviteMessageHtmlForEditor());
    setSendError("");
    setSending(false);
    setSendResult(null);
    setInvitedLog([]);
    setInvitedLogSummary(EMPTY_INVITE_LOG_SUMMARY);
    setInvitedLogLoading(false);
    setInvitedLogError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const loadInvitedLog = useCallback(async () => {
    const t = String(authToken || "").trim();
    const jp = String(jobPostId || "").trim();
    if (!t || !jp) return;
    setInvitedLogLoading(true);
    setInvitedLogError("");
    try {
      const { invites, summary } = await listHiringApplicantInvites(t, jp);
      const nextInvites = Array.isArray(invites) ? invites : [];
      setInvitedLog(nextInvites);
      setInvitedLogSummary(normalizeInviteLogSummary(summary, nextInvites));
    } catch (e) {
      setInvitedLog([]);
      setInvitedLogSummary(EMPTY_INVITE_LOG_SUMMARY);
      setInvitedLogError(e instanceof Error ? e.message : "Could not load invites.");
    } finally {
      setInvitedLogLoading(false);
    }
  }, [authToken, jobPostId]);

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    if (initialTab && INVITE_TABS.some((tab) => tab.id === initialTab)) {
      setActiveTab(initialTab);
    }
  }, [open, resetForm, initialTab]);

  useEffect(() => {
    if (!open || activeTab !== "invited-log" || !jobActive) return undefined;
    loadInvitedLog();
    return undefined;
  }, [open, activeTab, jobActive, loadInvitedLog]);

  useEffect(() => {
    if (!open || activeTab !== "invited-log" || invitedLogSummary.pending <= 0) return undefined;
    const timer = window.setInterval(() => {
      loadInvitedLog();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [open, activeTab, invitedLogSummary.pending, loadInvitedLog]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const validManualRows = useMemo(
    () =>
      manualRows.filter(
        (row) => String(row.name || "").trim() && isValidInviteEmail(row.email)
      ),
    [manualRows]
  );

  const validCsvRows = useMemo(() => validRecipientsFromCsvRows(csvRows), [csvRows]);
  const recipients = useMemo(
    () => mergeRecipients(validCsvRows, validManualRows),
    [validCsvRows, validManualRows]
  );

  const canSend =
    jobActive &&
    !sending &&
    !sendResult &&
    recipients.length > 0 &&
    String(subject || "").trim() &&
    getEditorTextLength(messageHtml) > 0;

  const handleCsvChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSendError("");
    setCsvRows([]);
    setCsvParseErrors([]);
    setCsvFileName(file.name);

    try {
      const text = await file.text();
      const parsed = parseApplicantInviteCsv(text);
      setCsvRows(parsed.rows);
      setCsvParseErrors(parsed.errors);
      if (parsed.errors.includes("empty_file") || parsed.errors.includes("bad_header")) {
        setSendError("We couldn't read that CSV — use the name,email template and try again.");
      } else if (validRecipientsFromCsvRows(parsed.rows).length === 0) {
        setSendError("We couldn't find any valid rows — check the template and try again.");
      }
    } catch {
      setCsvRows([]);
      setCsvParseErrors(["read_failed"]);
      setSendError("We couldn't read that file — try the template and upload again.");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCsvUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleManualRowChange = (index, field, value) => {
    setManualRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleAddManualRow = () => {
    setManualRows((prev) => [...prev, { ...EMPTY_MANUAL_ROW }]);
  };

  const handleRemoveManualRow = (index) => {
    setManualRows((prev) => {
      if (prev.length <= 1) return [{ ...EMPTY_MANUAL_ROW }];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRemoveCsvRow = (lineNumber) => {
    setCsvRows((prev) => prev.filter((row) => row.lineNumber !== lineNumber));
  };

  const handleMessageBlur = useCallback(() => {
    setMessageHtml((prev) => highlightInvitePlaceholderTokensInHtml(prev));
  }, []);

  const handleSend = async () => {
    if (!canSend) return;

    if (recipients.length > INVITE_LARGE_BATCH_CONFIRM_THRESHOLD) {
      const confirmed = window.confirm(
        `You're about to email ${recipients.length} people. We'll send them gradually and notify you when it's done.`
      );
      if (!confirmed) return;
    }

    setSending(true);
    setSendError("");
    try {
      const result = await postHiringApplicantInvites(authToken, jobPostId, {
        recipients,
        subject: subject.trim(),
        bodyHtml: buildApplicantInviteBodyHtml(stripInviteTokenHighlightSpans(messageHtml)),
      });
      setSendResult(result);
      if (result.queued > 0) {
        loadInvitedLog();
      }
    } catch (e) {
      setSendError(e instanceof Error ? e.message : "Something went wrong — try again.");
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (sending) return;
    onRequestClose();
  };

  if (!open) return null;

  const inactive = !jobActive;

  return (
    <div
      className="apply-help-overlay apply-shine-overlay--cancel-only hiring-role-invite-lightbox"
      role="presentation"
    >
      <div
        className={`apply-help-modal hiring-role-invite-lightbox__dialog${
          inactive ? " hiring-role-invite-lightbox__dialog--inactive" : ""
        }${sendResult ? " hiring-role-invite-lightbox__dialog--success" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hiring-role-invite-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="hiring-role-invite-lightbox__head">
          <h2 id="hiring-role-invite-title" className="hiring-role-invite-lightbox__title">
            Invite applicants
          </h2>
        </header>

        {inactive ? (
          <>
            <div className="hiring-role-invite-lightbox__body">
              <p className="hiring-role-invite-lightbox__lead">
                You need to activate this job before you can invite applicants.
              </p>
              <p className="hiring-role-invite-lightbox__hint">
                Set the job to <strong>Active</strong> in role settings above, then come back here.
              </p>
            </div>
            <footer className="hiring-role-invite-lightbox__footer">
              <button type="button" className="hiring-role-invite-cancel-btn" onClick={handleClose}>
                Cancel
              </button>
            </footer>
          </>
        ) : sendResult ? (
          <>
            <div className="hiring-role-invite-lightbox__body">
              <p className="hiring-role-invite-lightbox__success-lead">
                {sendResult.queued > 0
                  ? "We're sending your invites now."
                  : "We didn't queue any new invites this time."}
              </p>
              {sendResult.queued > 0 ? (
                <p className="hiring-role-invite-lightbox__hint">
                  We&apos;ll let you know once they&apos;re done. You can close this and keep working.
                </p>
              ) : null}
              {(sendResult.queued > 0 || sendResult.skipped > 0) && (
                <ul className="hiring-role-invite-summary" aria-label="Invite queue summary">
                  <li>
                    <strong>{sendResult.queued}</strong> queued
                  </li>
                  <li>
                    <strong>{sendResult.skipped}</strong> skipped
                  </li>
                </ul>
              )}
            </div>
            <footer className="hiring-role-invite-lightbox__footer">
              <button type="button" className="large-filled-button" onClick={handleClose}>
                Done
              </button>
            </footer>
          </>
        ) : (
          <>
            <div className="hiring-role-invite-lightbox__body">
              <p className="hiring-role-invite-lightbox__lead">
                Invite applicants to apply on JOBUWIN for <strong>{jobTitle || "this role"}</strong>.
                They&apos;ll get an email with a link.
              </p>

              <div className="hiring-role-invite-tab-nav" role="tablist" aria-label="Invite applicants sections">
                {INVITE_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    id={`hiring-role-invite-tab-${tab.id}`}
                    className={`hiring-role-invite-tab-btn${
                      activeTab === tab.id ? " hiring-role-invite-tab-btn--active" : ""
                    }`}
                    aria-selected={activeTab === tab.id}
                    aria-controls={`hiring-role-invite-panel-${tab.id}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === "new-applicants" ? (
                <div
                  className="hiring-role-invite-tab-panel"
                  role="tabpanel"
                  id="hiring-role-invite-panel-new-applicants"
                  aria-labelledby="hiring-role-invite-tab-new-applicants"
                >
                  <p className="hiring-role-invite-tab-panel__intro">
                    Add people with a CSV upload or enter them manually. You can edit the invitation email in the{" "}
                    <strong>Invitation email</strong> tab.
                  </p>

                  <section className="hiring-role-invite-section" aria-labelledby="hiring-role-invite-csv-title">
                    <div className="hiring-role-invite-section__head">
                      <h4 id="hiring-role-invite-csv-title">Upload a CSV</h4>
                      <button
                        type="button"
                        className="hiring-role-invite-link-btn"
                        onClick={downloadApplicantInviteCsvTemplate}
                      >
                        Download template
                      </button>
                    </div>
                    <p className="hiring-role-invite-section__hint">
                      Columns: name, email (header row required). Only one file at a time — re-uploading will overwrite
                      what&apos;s here.
                    </p>
                    <div className="hiring-role-invite-upload-row">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        className="hiring-role-invite-file-input"
                        onChange={handleCsvChange}
                      />
                      <button
                        type="button"
                        className="hiring-role-invite-upload-btn"
                        onClick={handleCsvUploadClick}
                      >
                        <Upload size={18} strokeWidth={1.75} aria-hidden />
                        {csvFileName ? "Choose a different file" : "Choose CSV file"}
                      </button>
                      {csvFileName ? <span className="hiring-role-invite-file-name">{csvFileName}</span> : null}
                    </div>
                    {csvRows.length > 0 ? (
                      <div className="hiring-role-invite-preview-wrap">
                        <p className="hiring-role-invite-preview-count">
                          {validCsvRows.length} valid row{validCsvRows.length === 1 ? "" : "s"} from CSV
                          {csvParseErrors.length > 0 ? ` · ${csvParseErrors.length} with issues` : ""}
                        </p>
                        <table className="hiring-role-invite-preview-table">
                          <thead>
                            <tr>
                              <th scope="col">Name</th>
                              <th scope="col">Email</th>
                              <th scope="col">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {csvRows.slice(0, 12).map((row) => (
                              <tr key={`csv-${row.lineNumber}-${row.email}`}>
                                <td>{row.name || "—"}</td>
                                <td>{row.email || "—"}</td>
                                <td>
                                  <div className="hiring-role-invite-preview-status-cell">
                                    {row.errors.length === 0 ? (
                                      <span className="hiring-role-invite-tag hiring-role-invite-tag--ok">Ready</span>
                                    ) : (
                                      <span className="hiring-role-invite-tag hiring-role-invite-tag--warn">
                                        {row.errors.includes("invalid_email")
                                          ? "Invalid email"
                                          : row.errors.includes("missing_name")
                                            ? "Missing name"
                                            : row.errors.includes("duplicate")
                                              ? "Duplicate"
                                              : "Check row"}
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      className="hiring-role-invite-manual-remove-btn hiring-role-invite-preview-remove-btn"
                                      aria-label={`Remove ${row.name || row.email || `CSV row ${row.lineNumber}`}`}
                                      onClick={() => handleRemoveCsvRow(row.lineNumber)}
                                    >
                                      <Trash2 size={16} strokeWidth={1.75} aria-hidden />
                                      <span className="profile-manage-sr-only">Remove</span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                  </section>

                  <section className="hiring-role-invite-section" aria-labelledby="hiring-role-invite-manual-title">
                    <h4 id="hiring-role-invite-manual-title">Add people manually</h4>
                    <div className="hiring-role-invite-manual-rows">
                      <div className="hiring-role-invite-manual-head" aria-hidden="true">
                        <span className="job-search-label">Name</span>
                        <span className="job-search-label">Email</span>
                        <span className="hiring-role-invite-manual-head__spacer" />
                      </div>
                      {manualRows.map((row, index) => (
                        <div className="hiring-role-invite-manual-row" key={`manual-${index}`}>
                          <input
                            type="text"
                            className="hiring-role-invite-manual-input"
                            value={row.name}
                            autoComplete="name"
                            placeholder="Full name"
                            aria-label={`Name${manualRows.length > 1 ? `, person ${index + 1}` : ""}`}
                            onChange={(event) => handleManualRowChange(index, "name", event.target.value)}
                          />
                          <input
                            type="email"
                            className="hiring-role-invite-manual-input"
                            value={row.email}
                            autoComplete="email"
                            placeholder="name@example.com"
                            aria-label={`Email${manualRows.length > 1 ? `, person ${index + 1}` : ""}`}
                            onChange={(event) => handleManualRowChange(index, "email", event.target.value)}
                          />
                          <button
                            type="button"
                            className="hiring-role-invite-manual-remove-btn"
                            aria-label={`Remove row ${index + 1}`}
                            onClick={() => handleRemoveManualRow(index)}
                          >
                            <Trash2 size={16} strokeWidth={1.75} aria-hidden />
                            <span className="profile-manage-sr-only">Remove</span>
                          </button>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="hiring-role-invite-add-row-btn" onClick={handleAddManualRow}>
                      <Plus size={16} strokeWidth={1.75} aria-hidden />
                      Add another
                    </button>
                    <p className="hiring-role-invite-recipient-count" role="status">
                      {recipients.length} recipient{recipients.length === 1 ? "" : "s"} ready to invite
                    </p>
                  </section>
                </div>
              ) : null}

              {activeTab === "invitation-email" ? (
                <div
                  className="hiring-role-invite-tab-panel"
                  role="tabpanel"
                  id="hiring-role-invite-panel-invitation-email"
                  aria-labelledby="hiring-role-invite-tab-invitation-email"
                >
                  <label className="hiring-role-invite-field hiring-role-invite-field--full">
                    <span className="job-search-label">Subject</span>
                    <input
                      type="text"
                      value={subject}
                      maxLength={200}
                      onChange={(event) => setSubject(event.target.value)}
                    />
                  </label>
                  <div className="hiring-role-invite-email-compose">
                    <section
                      className="hiring-role-invite-email-fixed-block"
                      aria-label="Email opening, added to every invite"
                    >
                      <p className="hiring-role-invite-email-fixed-label">Opening — added to every invite</p>
                      <p className="hiring-role-invite-email-open">
                        <strong>Hi </strong>
                        <InviteEmailToken>{APPLICANT_INVITE_PLACEHOLDER_APPLICANT_NAME}</InviteEmailToken>
                        <strong>,</strong>
                      </p>
                    </section>

                    <section className="hiring-role-invite-email-editor-block">
                      <JobCreateRichTextField
                        label="Main message — yours to edit"
                        hideCounter
                        value={messageHtml}
                        onChange={(event) =>
                          setMessageHtml(normalizeRichTextStorage(event.target.value))
                        }
                        onBlur={handleMessageBlur}
                        placeholder="Tell them why you'd like them to apply on JOBUWIN."
                        maxChars={INVITE_MESSAGE_MAX_CHARS}
                        ariaLabel="Main invite message"
                      />
                      <span className="saved-search-meta job-create-counter hiring-role-invite-email-counter">
                        {messageCharsRemaining} characters remaining
                      </span>
                    </section>

                    <section
                      className="hiring-role-invite-email-fixed-block"
                      aria-label="Email closing, added to every invite"
                    >
                      <p className="hiring-role-invite-email-fixed-label">Closing — added to every invite</p>
                      <div
                        className="hiring-role-invite-email-fixed-copy"
                        dangerouslySetInnerHTML={{ __html: DEFAULT_APPLICANT_INVITE_FIXED_CLOSING_HTML }}
                      />
                      <p className="hiring-role-invite-email-link-intro">{APPLICANT_INVITE_LINK_INTRO}</p>
                      <div className="hiring-role-invite-email-cta-preview" aria-hidden="true">
                        <span className="hiring-role-invite-email-cta-preview__btn">{APPLICANT_INVITE_CTA_LABEL}</span>
                      </div>
                      <p className="hiring-role-invite-email-link">
                        <InviteEmailToken>{APPLICANT_INVITE_PLACEHOLDER_APPLICATION_LINK}</InviteEmailToken>
                      </p>
                      <p className="hiring-role-invite-email-signature">
                        <strong>
                          Kind regards,
                          <br />
                          <br />
                          <InviteEmailSignatureLine
                            hiringManagerName={hiringManagerName}
                            companyName={companyName}
                          />
                        </strong>
                      </p>
                      <p className="hiring-role-invite-email-disclaimer">{APPLICANT_INVITE_DISCLAIMER}</p>
                    </section>
                  </div>
                </div>
              ) : null}

              {activeTab === "invited-log" ? (
                <div
                  className="hiring-role-invite-tab-panel"
                  role="tabpanel"
                  id="hiring-role-invite-panel-invited-log"
                  aria-labelledby="hiring-role-invite-tab-invited-log"
                >
                  {invitedLogLoading ? (
                    <p className="hiring-role-invite-log-status">Loading invites…</p>
                  ) : invitedLogError ? (
                    <p className="hiring-role-invite-lightbox__error" role="alert">
                      {invitedLogError}
                    </p>
                  ) : invitedLog.length === 0 ? (
                    <p className="hiring-role-invite-log-empty">No invites sent for this job yet.</p>
                  ) : (
                    <div className="hiring-role-invite-preview-wrap">
                      <p className="hiring-role-invite-log-summary" role="status">
                        {formatInviteLogSummary(invitedLogSummary)}
                      </p>
                      <table className="hiring-role-invite-preview-table hiring-role-invite-log-table">
                        <thead>
                          <tr>
                            <th scope="col">Name</th>
                            <th scope="col">Email</th>
                            <th scope="col">Status</th>
                            <th scope="col">Last updated</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invitedLog.map((invite) => (
                            <tr key={invite.id}>
                              <td>{invite.invitedName || "—"}</td>
                              <td>{invite.invitedEmail || "—"}</td>
                              <td>
                                <span
                                  className={`hiring-role-invite-tag hiring-role-invite-tag--${invite.status === "sent" ? "ok" : invite.status === "failed" ? "failed" : "warn"}`}
                                >
                                  {inviteStatusLabel(invite.status)}
                                </span>
                              </td>
                              <td>{formatInviteDate(inviteLastUpdatedAt(invite))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}

              {sendError ? (
                <p className="hiring-role-invite-lightbox__error" role="alert">
                  {sendError}
                </p>
              ) : null}
            </div>
            <footer className="hiring-role-invite-lightbox__footer">
              <button
                type="button"
                className="hiring-role-invite-cancel-btn"
                onClick={handleClose}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="large-filled-button"
                onClick={handleSend}
                disabled={!canSend}
              >
                {sending
                  ? recipients.length > INVITE_LARGE_BATCH_CONFIRM_THRESHOLD
                    ? "Sending… this can take a few minutes for large lists"
                    : "Sending…"
                  : "Send invites"}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
